import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Settings,
  Star,
  Info,
  MonetizationOn,
  AccessTime,
  Today,
  Wifi,
  Security,
  Warning,
  Save,
  Cancel,
  Hotel,
  SportsVolleyball,
  ContentCut,
  Build,
  Nature,
  Restaurant,
  Pets,
  WbSunny,
  NightsStay,
  LocalShipping,
  AccountBalance,
  Timer,
  Groups,
} from '@mui/icons-material';

export { }; // Make this a module

interface Board {
  id: string;
  name: string;
  type: string;
  description: string;
  slug?: string;
  capacity: number;
  availabilitySettings: any;
  pricing: any;
  amenities?: string[];
  policies?: any;
  rules?: string;
  cancellationPolicy?: string;
}

interface BoardEditDialogProps {
  open: boolean;
  board: Board | null;
  onClose: () => void;
  onSave: (boardData: Partial<Board>) => void;
}

// Type-specific configuration
const boardTypeConfig = {
  rooms: {
    label: '🏨 Habitaciones',
    icon: <Hotel />,
    color: 'primary',
    pricingFields: ['basePrice', 'perDay'],
    hasCheckInOut: true,
    suggestedAmenities: ['WiFi gratuito', 'Aire acondicionado', 'TV LED', 'Baño privado', 'Desayuno incluido', 'Minibar', 'Servicio de habitaciones', 'Caja fuerte'],
    suggestedPolicies: ['checkIn', 'checkOut', 'cancellation'],
    rulesPlaceholder: 'Ej: No se permiten mascotas. Prohibido fumar en habitaciones. Silencio después de las 23hs.',
    cancellationPlaceholder: 'Ej: Cancelación gratuita hasta 24 horas antes del check-in. Después se cobra el 50% del valor.',
  },
  cabins: {
    label: '🛖 Cabañas',
    icon: <Nature />,
    color: 'success',
    pricingFields: ['perDay'],
    hasCheckInOut: true,
    suggestedAmenities: ['Baño privado', 'Cocina completa', 'Heladera', 'TV LED', 'Vista al lago', 'Terraza privada', 'Parrilla', 'Pet friendly'],
    suggestedPolicies: ['checkIn', 'checkOut', 'pets', 'cancellation'],
    rulesPlaceholder: 'Ej: No se permiten fiestas. Mascotas deben estar con correa. Respetar el silencio.',
    cancellationPlaceholder: 'Ej: Cancelación gratuita hasta 72 horas antes. Después se cobra el 70% del valor.',
  },
  camping: {
    label: '⛺ Camping',
    icon: <Nature />,
    color: 'warning',
    pricingFields: ['perDay'],
    hasCheckInOut: true,
    suggestedAmenities: ['Fogón incluido', 'Mesa y bancos', 'Agua potable cercana', 'Electricidad', 'Baños compartidos', 'Duchas calientes', 'Pet friendly'],
    suggestedPolicies: ['checkIn', 'checkOut', 'pets', 'cancellation'],
    rulesPlaceholder: 'Ej: No hacer fogatas fuera de espacios designados. Mantener el área limpia.',
    cancellationPlaceholder: 'Ej: Cancelación gratuita hasta 48 horas antes. Después se cobra el 50%.',
  },
  salons: {
    label: '🍽️ Salones',
    icon: <Restaurant />,
    color: 'secondary',
    pricingFields: ['basePrice', 'perHour'],
    hasCheckInOut: false,
    suggestedAmenities: ['Aire acondicionado', 'Sonido incluido', 'Proyector', 'Sillas y mesas', 'Cocina disponible', 'Estacionamiento', 'Seguridad'],
    suggestedPolicies: ['cancellation'],
    rulesPlaceholder: 'Ej: Límite de horario hasta las 3am. No se permite pirotecnia.',
    cancellationPlaceholder: 'Ej: Cancelación con devolución del 50% hasta 7 días antes del evento.',
  },
  sports_fields: {
    label: '⚽ Canchas Deportivas',
    icon: <SportsVolleyball />,
    color: 'info',
    pricingFields: ['perHour'],
    hasCheckInOut: false,
    hasLightingSurcharge: true,
    suggestedAmenities: ['Iluminación nocturna', 'Vestuarios', 'Duchas', 'Estacionamiento', 'Alquiler de pelotas', 'Agua disponible'],
    suggestedPolicies: ['cancellation'],
    rulesPlaceholder: 'Ej: Usar calzado deportivo adecuado. No ingresar con bebidas alcohólicas.',
    cancellationPlaceholder: 'Ej: Cancelación gratuita hasta 4 horas antes. Lluvia: reprogramación sin cargo.',
  },
  appointments: {
    label: '💇 Servicios / Turnos',
    icon: <ContentCut />,
    color: 'error',
    pricingFields: ['basePrice'],
    hasCheckInOut: false,
    hasServiceDuration: true,
    suggestedAmenities: [],
    suggestedPolicies: ['cancellation'],
    rulesPlaceholder: 'Ej: Presentarse 10 minutos antes. Cancelar con al menos 2 horas de anticipación.',
    cancellationPlaceholder: 'Ej: Primera cancelación sin cargo. Más de 2 cancelaciones mensuales genera cargo del 50%.',
  },
  equipment: {
    label: '⚙️ Alquiler de Equipos',
    icon: <Build />,
    color: 'primary',
    pricingFields: ['perHour', 'perDay'],
    hasCheckInOut: false,
    hasDeposit: true,
    hasDelivery: true,
    suggestedAmenities: ['Entrega a domicilio', 'Capacitación incluida', 'Seguro incluido', 'Combustible incluido', 'Mantenimiento incluido'],
    suggestedPolicies: ['deposit', 'cancellation'],
    rulesPlaceholder: 'Ej: El equipo debe devolverse en las mismas condiciones. Daños se cobran al tarjetahabiente.',
    cancellationPlaceholder: 'Ej: Cancelación hasta 24hs antes devuelve el 100%. Menos de 24hs devuelve el 50%.',
  },
};

const defaultConfig = {
  label: '📋 General',
  icon: <Star />,
  color: 'primary',
  pricingFields: ['basePrice', 'perHour', 'perDay'],
  hasCheckInOut: false,
  suggestedAmenities: [],
  suggestedPolicies: ['cancellation'],
  rulesPlaceholder: 'Reglas y condiciones del servicio',
  cancellationPlaceholder: 'Política de cancelación y reembolsos',
};

const BoardEditDialog: React.FC<BoardEditDialogProps> = ({
  open,
  board,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [editData, setEditData] = useState<Partial<Board>>({});
  const [amenitiesRaw, setAmenitiesRaw] = useState<string>('');

  // Get type-specific config
  const typeConfig = board?.type
    ? (boardTypeConfig as any)[board.type] || defaultConfig
    : defaultConfig;

  useEffect(() => {
    if (board && open) {
      setEditData({
        ...board,
        amenities: Array.isArray(board.amenities) ? board.amenities : [],
        slug: board.slug || '',
        policies: board.policies || {},
      });
      setAmenitiesRaw(Array.isArray(board.amenities) ? board.amenities.join(', ') : '');
    }
  }, [board, open]);

  const handleSave = () => {
    onSave(editData);
  };

  const updateField = (field: keyof Board, value: any) => {
    setEditData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const updatePricing = (pricingField: string, value: number) => {
    setEditData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [pricingField]: value,
      },
    }));
  };

  const updatePolicies = (policyField: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      policies: {
        ...prev.policies,
        [policyField]: value,
      },
    }));
  };

  const updateAmenities = (amenitiesString: string) => {
    const amenities = amenitiesString
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    setEditData(prev => ({
      ...prev,
      amenities,
    }));
  };

  const addSuggestedAmenity = (amenity: string) => {
    const currentAmenities = editData.amenities || [];
    if (!currentAmenities.includes(amenity)) {
      const newAmenities = [...currentAmenities, amenity];
      setEditData(prev => ({
        ...prev,
        amenities: newAmenities,
      }));
      setAmenitiesRaw(newAmenities.join(', '));
    }
  };

  const removeAmenity = (amenity: string) => {
    const newAmenities = (editData.amenities || []).filter(a => a !== amenity);
    setEditData(prev => ({
      ...prev,
      amenities: newAmenities,
    }));
    setAmenitiesRaw(newAmenities.join(', '));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${typeConfig.color === 'primary' ? '#667eea' : typeConfig.color === 'success' ? '#38a169' : typeConfig.color === 'warning' ? '#dd6b20' : typeConfig.color === 'info' ? '#3182ce' : typeConfig.color === 'error' ? '#e53e3e' : typeConfig.color === 'secondary' ? '#805ad5' : '#667eea'} 0%, ${typeConfig.color === 'primary' ? '#764ba2' : typeConfig.color === 'success' ? '#2f855a' : typeConfig.color === 'warning' ? '#c05621' : typeConfig.color === 'info' ? '#2b6cb0' : typeConfig.color === 'error' ? '#c53030' : typeConfig.color === 'secondary' ? '#6b46c1' : '#764ba2'} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3,
        }}
      >
        <Settings sx={{ fontSize: 30 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {typeConfig.label} - Configuración
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Personaliza {board?.name || 'tu agenda'} según tu negocio
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          🎯 <strong>Tip:</strong> Los campos se adaptan según el tipo de agenda ({typeConfig.label}).
          Completá solo lo que aplique a tu negocio.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Basic Information Section */}
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              📝 Información Básica
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nombre del Servicio"
                fullWidth
                variant="outlined"
                value={editData.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                InputProps={{
                  startAdornment: <Star sx={{ mr: 1, color: 'primary.main' }} />,
                }}
              />
              <TextField
                label="Descripción Atractiva"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={editData.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                helperText="Describe lo que hace especial a tu servicio"
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Capacidad Máxima"
                  type="number"
                  variant="outlined"
                  value={editData.capacity || ''}
                  onChange={(e) => updateField('capacity', parseInt(e.target.value) || 1)}
                  InputProps={{
                    startAdornment: <Groups sx={{ mr: 1, color: 'primary.main' }} />,
                  }}
                  helperText="Personas/unidades"
                  sx={{ flex: '1 1 200px' }}
                />
                <TextField
                  label="URL Amigable (Slug)"
                  variant="outlined"
                  value={editData.slug || ''}
                  onChange={(e) => updateField('slug', e.target.value)}
                  helperText="Para compartir (ej: suite-premium)"
                  sx={{ flex: '1 1 300px' }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Pricing Section - Type Specific */}
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: isDark ? 'rgba(46,125,50,0.15)' : 'success.50' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
              💰 Configuración de Precios
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {board?.type === 'appointments'
                ? 'Precio por cada turno o sesión'
                : board?.type === 'sports_fields'
                  ? 'Precio por hora de alquiler'
                  : board?.type === 'rooms' || board?.type === 'cabins' || board?.type === 'camping'
                    ? 'Precio por día/noche de estadía'
                    : board?.type === 'equipment'
                      ? 'Precios por hora y por día completo'
                      : 'Establece los precios según tu modelo de negocio'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {typeConfig.pricingFields.includes('basePrice') && (
                <Card sx={{ minWidth: 200, border: '2px solid', borderColor: 'success.main' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <MonetizationOn sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <TextField
                      label={board?.type === 'appointments' ? 'Precio por Turno' : 'Precio Base'}
                      type="number"
                      variant="outlined"
                      size="small"
                      value={editData.pricing?.basePrice || ''}
                      onChange={(e) => updatePricing('basePrice', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                      }}
                      helperText={board?.type === 'appointments' ? 'Por sesión' : 'Servicio completo'}
                    />
                  </CardContent>
                </Card>
              )}

              {typeConfig.pricingFields.includes('perHour') && (
                <Card sx={{ minWidth: 200, border: '2px solid', borderColor: 'info.main' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AccessTime sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <TextField
                      label="Precio por Hora"
                      type="number"
                      variant="outlined"
                      size="small"
                      value={editData.pricing?.perHour || ''}
                      onChange={(e) => updatePricing('perHour', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                      }}
                      helperText="Alquiler por hora"
                    />
                  </CardContent>
                </Card>
              )}

              {typeConfig.pricingFields.includes('perDay') && (
                <Card sx={{ minWidth: 200, border: '2px solid', borderColor: 'warning.main' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Today sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                    <TextField
                      label="Precio por Día"
                      type="number"
                      variant="outlined"
                      size="small"
                      value={editData.pricing?.perDay || ''}
                      onChange={(e) => updatePricing('perDay', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                      }}
                      helperText={board?.type === 'rooms' || board?.type === 'cabins' ? 'Por noche' : 'Alquiler diario'}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Sports fields: Night lighting surcharge */}
              {typeConfig.hasLightingSurcharge && (
                <Card sx={{ minWidth: 200, border: '2px solid', borderColor: 'secondary.main' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <NightsStay sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                    <TextField
                      label="Recargo Nocturno"
                      type="number"
                      variant="outlined"
                      size="small"
                      value={editData.pricing?.nightSurcharge || ''}
                      onChange={(e) => updatePricing('nightSurcharge', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                      }}
                      helperText="Extra por iluminación"
                    />
                  </CardContent>
                </Card>
              )}

              {/* Equipment: Deposit */}
              {typeConfig.hasDeposit && (
                <Card sx={{ minWidth: 200, border: '2px solid', borderColor: 'error.main' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AccountBalance sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                    <TextField
                      label="Depósito/Garantía"
                      type="number"
                      variant="outlined"
                      size="small"
                      value={editData.pricing?.deposit || ''}
                      onChange={(e) => updatePricing('deposit', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                      }}
                      helperText="Reembolsable"
                    />
                  </CardContent>
                </Card>
              )}
            </Box>
          </Paper>

          {/* Check-in/Check-out for lodging types */}
          {typeConfig.hasCheckInOut && (
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: isDark ? 'rgba(2,136,209,0.15)' : 'info.50' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.main' }}>
                🕐 Horarios de Check-in / Check-out
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WbSunny color="warning" />
                  <TextField
                    label="Check-in"
                    type="time"
                    variant="outlined"
                    size="small"
                    value={editData.policies?.checkIn || '14:00'}
                    onChange={(e) => updatePolicies('checkIn', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NightsStay color="primary" />
                  <TextField
                    label="Check-out"
                    type="time"
                    variant="outlined"
                    size="small"
                    value={editData.policies?.checkOut || '12:00'}
                    onChange={(e) => updatePolicies('checkOut', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                  />
                </Box>
              </Box>
            </Paper>
          )}

          {/* Service Duration for appointments */}
          {typeConfig.hasServiceDuration && (
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: isDark ? 'rgba(2,136,209,0.15)' : 'info.50' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.main' }}>
                ⏱️ Duración del Servicio
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Timer color="info" />
                <TextField
                  label="Duración por turno (minutos)"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={editData.policies?.serviceDuration || 60}
                  onChange={(e) => updatePolicies('serviceDuration', e.target.value)}
                  helperText="Tiempo promedio de cada cita"
                  sx={{ width: 250 }}
                />
              </Box>
            </Paper>
          )}

          {/* Delivery for equipment */}
          {typeConfig.hasDelivery && (
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: isDark ? 'rgba(2,136,209,0.15)' : 'info.50' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.main' }}>
                🚚 Opciones de Entrega
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editData.policies?.hasDelivery || false}
                      onChange={(e) => updatePolicies('hasDelivery', e.target.checked ? 'true' : '')}
                      color="primary"
                    />
                  }
                  label="Ofrece entrega a domicilio"
                />
                {editData.policies?.hasDelivery && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <LocalShipping color="info" />
                    <TextField
                      label="Costo de envío"
                      type="number"
                      variant="outlined"
                      size="small"
                      value={editData.pricing?.deliveryFee || ''}
                      onChange={(e) => updatePricing('deliveryFee', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                      }}
                      helperText="Por entrega"
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {/* Amenities Section - with suggestions based on type */}
          {typeConfig.suggestedAmenities.length > 0 && (
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: isDark ? 'rgba(103,58,183,0.15)' : 'primary.50' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                ✨ Amenities y Servicios
              </Typography>

              {/* Quick add suggestions */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Hacé clic para agregar:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {typeConfig.suggestedAmenities.map((amenity: string, index: number) => {
                  const isSelected = (editData.amenities || []).includes(amenity);
                  return (
                    <Chip
                      key={index}
                      label={amenity}
                      variant={isSelected ? "filled" : "outlined"}
                      color={isSelected ? "primary" : "default"}
                      onClick={() => isSelected ? removeAmenity(amenity) : addSuggestedAmenity(amenity)}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>

              <Divider sx={{ my: 2 }} />

              <TextField
                label="Amenities personalizados"
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                value={amenitiesRaw}
                onChange={(e) => {
                  const rawText = e.target.value;
                  setAmenitiesRaw(rawText);
                  updateAmenities(rawText);
                }}
                helperText="Agregá otros separados por coma"
                InputProps={{
                  startAdornment: <Wifi sx={{ mr: 1, color: 'primary.main' }} />,
                }}
              />

              {editData.amenities && editData.amenities.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Amenities seleccionados ({editData.amenities.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {editData.amenities.map((amenity, index) => (
                      <Chip
                        key={index}
                        label={amenity}
                        onDelete={() => removeAmenity(amenity)}
                        color="primary"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          {/* Pet Policy for cabins/camping */}
          {(board?.type === 'cabins' || board?.type === 'camping') && (
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: isDark ? 'rgba(156,39,176,0.15)' : 'secondary.50' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}>
                <Pets /> Política de Mascotas
              </Typography>
              <TextField
                label="Política de mascotas"
                fullWidth
                variant="outlined"
                value={editData.policies?.pets || ''}
                onChange={(e) => updatePolicies('pets', e.target.value)}
                helperText="Ej: Mascotas permitidas con cargo adicional de $500/día. Deben estar con correa."
              />
            </Paper>
          )}

          {/* Policies and Rules Section */}
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: isDark ? 'rgba(255,160,0,0.15)' : 'warning.50' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
              📋 Reglas y Políticas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Establece las reglas claras para tus clientes
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Reglas Importantes"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={editData.rules || ''}
                onChange={(e) => updateField('rules', e.target.value)}
                placeholder={typeConfig.rulesPlaceholder}
                InputProps={{
                  startAdornment: <Security sx={{ mr: 1, color: 'warning.main' }} />,
                }}
              />

              <TextField
                label="Política de Cancelación"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={editData.cancellationPolicy || ''}
                onChange={(e) => updateField('cancellationPolicy', e.target.value)}
                placeholder={typeConfig.cancellationPlaceholder}
                InputProps={{
                  startAdornment: <Warning sx={{ mr: 1, color: 'error.main' }} />,
                }}
              />
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<Cancel />}
          sx={{ mr: 2 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="large"
          startIcon={<Save />}
          sx={{
            background: `linear-gradient(135deg, ${typeConfig.color === 'primary' ? '#667eea' : typeConfig.color === 'success' ? '#38a169' : '#667eea'} 0%, ${typeConfig.color === 'primary' ? '#764ba2' : typeConfig.color === 'success' ? '#2f855a' : '#764ba2'} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${typeConfig.color === 'primary' ? '#5a6fd8' : typeConfig.color === 'success' ? '#2f855a' : '#5a6fd8'} 0%, ${typeConfig.color === 'primary' ? '#6a4190' : typeConfig.color === 'success' ? '#276749' : '#6a4190'} 100%)`,
            },
          }}
        >
          💾 Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoardEditDialog;